import React, { useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import useAuthAxios from "../hooks/useAuthAxios"
import Layout from "../components/layout/Layout"
import { useAuth } from "../context/AuthContext"
import ExpertStats from "../components/dashboard/ExpertStats"
import { getDocuments, getFileIcon } from "../utils/documentUtils"

const Dashboard = () => {
  const [stats, setStats] = useState(null)
  const [expertStats, setExpertStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const [statsError, setStatsError] = useState(null)
  const [expertStatsLoading, setExpertStatsLoading] = useState(true)
  const [expertStatsError, setExpertStatsError] = useState(null)
  const [rawStats, setRawStats] = useState(null)
  const { get } = useAuthAxios()
  const { isAdmin, appLookups, isGarage } = useAuth()

  const statusLookups = useMemo(
    () =>
      appLookups?.filter(
        lookup =>
          lookup.Table === "Status" &&
          (!isGarage() || [10, 20, 40, 50, 60, 70, 80, 90, 95, 100].includes(lookup.Code))
      ) || [],
    [appLookups, isGarage]
  )

  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true)
      setStatsError(null)
      try {
        const response = await get("/stats")
        setRawStats(response.data)
      } catch (error) {
        console.error("Αποτυχία λήψης στατιστικών:", error)
        setStatsError("Αποτυχία φόρτωσης στατιστικών")
      } finally {
        setStatsLoading(false)
      }
    }

    const fetchExpertStats = async () => {
      setExpertStatsLoading(true)
      setExpertStatsError(null)
      try {
        const response = await get("/stats", { params: { ExpertID: 0 } })
        setExpertStats(response.data)
      } catch (error) {
        console.error("Αποτυχία λήψης στατιστικών ανά πραγματογνώμονα:", error)
        setExpertStatsError("Αποτυχία φόρτωσης στατιστικών ανά πραγματογνώμονα")
      } finally {
        setExpertStatsLoading(false)
      }
    }

    if (isAdmin()) {
      fetchStats()
      fetchExpertStats()
    } else {
      fetchStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [get, isAdmin])

  useEffect(() => {
    if (!appLookups || !rawStats) return

    const calculatedStats = statusLookups.reduce((acc, status) => {
      acc[`status${status.Code}Repairs`] = rawStats
        ?.filter(stat => stat.StatusCode === status.Code)
        ?.reduce((sum, stat) => sum + stat.Counter, 0)
      return acc
    }, {})

    setStats(calculatedStats)
  }, [appLookups, rawStats, statusLookups])

  // Add custom styles for disabled fields
  useEffect(() => {
    const style = document.createElement("style")
    style.textContent = `
      .form-control:disabled {
        background-color:rgba(255, 255, 255, 0.3) !important;
      }
    `
    document.head.appendChild(style)
    return () => document.head.removeChild(style)
  }, [])

  return (
    <Layout>
      <div className="mb-4">
        {/* Statistics */}
        <div className="content-glass">
          <div className="dashboard-stats-container">
            {statusLookups
              .filter(status => isAdmin() || status.Code !== 0)
              .map(status => (
                <Link key={status.Code} to="/repairs" className="stats-link" state={{ status: status.Code }}>
                  <div
                    className={`stats-box bg-status-${status.Code} ${
                      stats?.[`status${status.Code}Repairs`] === 0 ? "greyed-out" : ""
                    }`}>
                    {statsLoading ? (
                      <div className="d-flex justify-content-center align-items-center h-100">
                        <div className="spinner-border text-light" role="status">
                          <span className="visually-hidden">Φόρτωση...</span>
                        </div>
                      </div>
                    ) : statsError ? (
                      <div className="text-white">
                        <i className="fas fa-exclamation-circle me-2" />
                        Σφάλμα
                      </div>
                    ) : (
                      <>
                        <h3>{stats?.[`status${status.Code}Repairs`] || 0}</h3>
                        <div>{status.Name}</div>
                        <i
                          className={`fas ${
                            status.Code === 0
                              ? "fa-file-circle-plus"
                              : status.Code === 1
                              ? "fa-clock"
                              : status.Code === 2
                              ? "fa-video"
                              : status.Code === 10
                              ? "fa-clipboard-check"
                              : status.Code === 99
                              ? "fa-ban"
                              : "fa-file"
                          } stats-icon`}
                        />
                      </>
                    )}
                  </div>
                </Link>
              ))}
          </div>
        </div>

        {/* Expert Statistics */}
        {isAdmin() && (
          <div className="mt-4">
            <div className="content-glass">
              <h4 className="mb-3">Στατιστικά ανά Πραγματογνώμονα</h4>
              <div className="row p-3">
                <ExpertStats data={expertStats} loading={expertStatsLoading} error={expertStatsError} />
              </div>
            </div>
          </div>
        )}

        {/* Χρήσιμα Έγγραφα */}
        {isGarage() && (
          <div className="mt-4">
            <div className="content-glass">
              <h4 className="mb-3">Χρήσιμα Έγγραφα</h4>
              <div className="row">
                {getDocuments().map((doc, index) => {
                  const handleDownload = () => {
                    try {
                      // Use doc.url (from require.context) for the asset
                      const link = document.createElement("a")
                      link.href = doc.url
                      link.setAttribute("download", doc.filename)
                      document.body.appendChild(link)
                      link.click()
                      link.remove()
                    } catch (error) {
                      console.error("Error downloading file:", error)
                    }
                  }

                  return (
                    <div key={index} className="col-md-6 col-lg-4 mb-3">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body d-flex flex-column">
                          <h6 className="card-title">
                            <i className={`${getFileIcon(doc.filename)} me-2`}></i>
                            {doc.name}
                          </h6>
                          <button onClick={handleDownload} className="btn btn-outline-primary btn-sm mt-auto">
                            <i className="fas fa-download me-1"></i>
                            Λήψη
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default Dashboard
